<template>
  <div class="version-history">
    <table class="vh-table">
      <colgroup>
        <col style="width: 63px" />   <!-- Serial -->
        <col style="width: 150px" />  <!-- Date -->
        <col style="width: 120px" />  <!-- User -->
        <col />                       <!-- Summary -->
        <!-- <col style="width: 90px" /> -->   <!-- Hash -->
        <col style="width: 90px" />   <!-- Tag -->
        <col style="width: 36px" />   <!-- Menu -->
      </colgroup>

      <thead>
        <tr>
          <th>Serial</th>
          <th>Date</th>
          <th>User</th>
          <th>Version summary</th>
          <!-- <th>Hash</th> -->
          <th>Tag</th>

          <!-- header icon -->
            <th class="header-menu">

           <button
                class="icon-btn add-tag-btn"
                title="Add new Tagged Version"
                @click="addTaggedVersion"
            >
                ＋
            </button>

           
            </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="v in sortedTaggedVersions"
          :key="v.hash"
        >
          <td class="serial">{{ v.serial }}</td>
          <td>{{ formatDate(v.created_at) }}</td>
          <td>{{ v.created_by }}</td>
          <td class="summary">{{ v.description }}</td>
          <!-- <td class="mono">{{ v.hash }}</td> -->
          <td>
            <span class="tag">{{ v.name}}</span>
          </td>

          <td class="menu">
            <button
              class="icon-btn"
              data-version-menu-trigger="true"
              @click.stop="openMenu(v, $event)"
            >
              ⋮
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
    </div>

    <div v-if="!loading && sortedTaggedVersions.length === 0" class="empty-state">
      No tagged versions
    </div>

    <!-- context menu -->
    <div
        v-if="menuVersion"
      ref="contextMenuRef"
        class="context-menu"
        :style="{
            top: `${menuY}px`,
            left: `${menuX - 160}px`  /* show to left, 160px width */
        }"
        @click.stop
        >
        <div @click="tag_restore(menuVersion.name)">
          Restore in Current Dataset
        </div>
        <!--
        <div @click="tag_download(menuVersion.name)">
          Download
        </div>
        -->
        <div @click="tag_delete(menuVersion.name)">
          Delete Tagged Version
        </div>
    </div>

  </div>

  <CreateDatasetTagDialog
    v-model:show="showCreateTagDialog"
    :dataset-id="datasetId"
    @created="handleTagCreated"
  />

  <ConfirmModal
    :show="showDeleteConfirmModal"
    :messageHeader="deleteConfirmHeader"
    :message="deleteConfirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="'Delete'"
    @confirmed="confirmDeleteTaggedVersion"
  />
</template>


<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import CreateDatasetTagDialog from '@/components/DatasetComponent/DatasetManagement/CreateDatasetTagDialog.vue'
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'
import { useToast } from 'vue-toast-notification'

const props = defineProps({
  datasetId: Number
})

const toast = useToast({
  position: 'top'
})

const emit = defineEmits([
  'add-tag'
])


/* Sorted newest → oldest */
const sortedTaggedVersions =  ref([]);
const loading = ref(true);

/* Context menu */
const menuVersion = ref(null)
const menuX = ref(0)
const menuY = ref(0)
const contextMenuRef = ref(null)

/* Create Tag Dialog */
const showCreateTagDialog = ref(false)
const showDeleteConfirmModal = ref(false)
const deleteConfirmHeader = ref('Delete Tagged Version')
const deleteConfirmMessage = ref('')
const pendingDeleteTagName = ref(null)

function openMenu(version, event) {
  menuVersion.value = version
  menuX.value = event.clientX
  menuY.value = event.clientY
}

function closeMenu() {
  menuVersion.value = null
}

function handleOutsideMenuClick(event) {
  if (!menuVersion.value) {
    return
  }

  const clickedInsideMenu = contextMenuRef.value?.contains(event.target)
  const clickedMenuTrigger = event.target instanceof Element
    && event.target.closest('[data-version-menu-trigger="true"]')

  if (!clickedInsideMenu && !clickedMenuTrigger) {
    closeMenu()
  }
}

function emitAction(action) {
  emit(action, menuVersion.value)
  closeMenu()
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function addTaggedVersion() {
  showCreateTagDialog.value = true
}

async function handleTagCreated({ response }) {
  await GetDatasetTags()
  emit('add-tag', response)
}

async function GetDatasetTags() {
  loading.value = true
  try {
    const dsConn = new DatastoreConnect()
    const response = await dsConn.api("version.tag.list", { dataset_id: props.datasetId })
    
    // Failsafe: extract tags from response.result or response.result (typo fallback)
    const tags = response?.result || []
    
    // Sort by date desc
    sortedTaggedVersions.value = tags.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )
    
  } catch (error) {
    console.error('Error fetching dataset tags:', error)
    sortedTaggedVersions.value = []
    return []
  } finally {
    loading.value = false
  }
}

async function tag_restore(tagName) {
  if (!menuVersion.value) return
  
  try {
    const dsConn = new DatastoreConnect()
    const response = await dsConn.api("version.tag.restore", {
      dataset_id: props.datasetId,
      name: tagName
    })
    
    if (response?.error) {
      toast.error('Failed to restore version: ' + response.error.message)
    } else {
      toast.success('Version restored successfully')
      await GetDatasetTags()
    }
  } catch (error) {
    console.error('Error restoring version:', error)
    toast.error('Failed to restore version: ' + error.message)
  } finally {
    closeMenu()
  }
}

/*
async function tag_download(tagName) {
  if (!menuVersion.value) return

  try {
    const dsConn = new DatastoreConnect()
    const response = await dsConn.api("version.tag.download", {
      dataset_id: props.datasetId,
      name: tagName
    })

    if (response?.error) {
      toast.error('Failed to download version: ' + response.error.message)
    } else if (response?.result?.download_url) {
      window.open(response.result.download_url, '_blank')
      toast.success('Download started')
    } else {
      console.log('Download response:', response)
      toast.success('Download initiated')
    }
  } catch (error) {
    console.error('Error downloading version:', error)
    toast.error('Failed to download version: ' + error.message)
  } finally {
    closeMenu()
  }
}
*/

async function tag_delete(tagName) {
  if (!menuVersion.value) return

  pendingDeleteTagName.value = tagName
  deleteConfirmMessage.value = `Are you sure you want to delete the tagged version "${menuVersion.value.name}"?`
  showDeleteConfirmModal.value = true
  closeMenu()
}

async function confirmDeleteTaggedVersion() {
  const tagName = pendingDeleteTagName.value
  if (!tagName) {
    showDeleteConfirmModal.value = false
    return
  }
  
  try {
    const dsConn = new DatastoreConnect()
    const response = await dsConn.api("version.tag.delete", {
      dataset_id: props.datasetId,
      name: tagName
    })
    
    if (response?.error) {
      toast.error('Failed to delete version: ' + response.error.message)
    } else {
      toast.success('Version deleted successfully')
      await GetDatasetTags()
    }
  } catch (error) {
    console.error('Error deleting version:', error)
    toast.error('Failed to delete version: ' + error.message)
  } finally {
    pendingDeleteTagName.value = null
    showDeleteConfirmModal.value = false
  }
}

onMounted(() => {
  GetDatasetTags()
  document.addEventListener('click', handleOutsideMenuClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideMenuClick)
})
</script>




<style lang="scss" scoped>

.version-history {
  position: relative;
  height: 100%;
  overflow-y: auto;
  font-size: 12px;
}

.vh-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header */
.vh-table thead th {
  position: sticky;
  top: 0;
  background: #fafafa;
  z-index: 2;
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  font-weight: 600;
}

/* Body */
.vh-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vh-table tbody tr:hover {
  background: #f6f6f6;
}

/* Helpers */
.mono {
  font-family: monospace;
}

.summary {
  white-space: nowrap;
}

.tag {
  background: #e8eefc;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
}

.changes {
  text-align: right;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 0.9rem;
}

/* Header icon */
.header-menu {
  text-align: center;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
}

/* Context menu */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  font-size: 14px;
  z-index: 1000;
}

.context-menu div {
  padding: 6px 12px;
  cursor: pointer;
}

.context-menu div:hover {
  background: #eee;
}


/* Header icon */
.header-menu {
  text-align: center;
}

.add-tag-btn {
  background-color: #4caf50; /* green */
  color: white;
  font-size: 16px;           /* bigger */
  width: 28px;
  height: 28px;
  border-radius: 50%;        /* circular */
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.add-tag-btn:hover {
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
}

/* Context menu */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  font-size: 12px;
  z-index: 1000;
  text-align: left;
}

.context-menu div {
  padding: 8px 12px;         /* slightly larger clickable area */
  cursor: pointer;
}

.context-menu div:hover {
  background: #eee;
}

</style>